import React, { FC, KeyboardEvent, useState } from 'react';
import { Add, DeleteOutline, EditOutlined, MoreVert } from '@mui/icons-material';
import {
  Button,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { VariableDto } from '@thingslog/repositories';
import { DashboardConfirmationDialog } from '../../confirmation-dialog/DashboardConfirmationDialog';
import { useTranslation } from 'react-i18next';

interface DashboardSidebarVariablesWidgetProps {
  widgetId: string;
  variables: VariableDto[];
  isDeletingVariable?: boolean;
  onAddVariable: () => void;
  onEditVariable: (variable: VariableDto) => void;
  onDeleteVariable: (variable: VariableDto, callbacks?: { onSuccess?: () => void }) => void;
}

const getVariableValues = (variable: VariableDto): string[] => {
  if (Array.isArray(variable.value)) {
    return variable.value.filter(Boolean);
  }

  return variable.value ? [variable.value] : [];
};

export const DashboardSidebarVariablesWidget: FC<DashboardSidebarVariablesWidgetProps> = ({
  widgetId,
  variables,
  isDeletingVariable = false,
  onAddVariable,
  onEditVariable,
  onDeleteVariable,
}: DashboardSidebarVariablesWidgetProps) => {
  const { t } = useTranslation();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedVariable, setSelectedVariable] = useState<VariableDto | null>(null);
  const [variablePendingDelete, setVariablePendingDelete] = useState<VariableDto | null>(null);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    variable: VariableDto
  ): void => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedVariable(variable);
  };

  const handleMenuClose = (): void => {
    setMenuAnchorEl(null);
    setSelectedVariable(null);
  };

  const handleEdit = (): void => {
    if (selectedVariable) {
      onEditVariable(selectedVariable);
    }

    handleMenuClose();
  };

  const handleDelete = (): void => {
    if (selectedVariable) {
      setVariablePendingDelete(selectedVariable);
    }

    handleMenuClose();
  };

  const handleDeleteCancel = (): void => {
    setVariablePendingDelete(null);
  };

  const handleDeleteConfirm = (): void => {
    if (!variablePendingDelete) {
      return;
    }

    onDeleteVariable(variablePendingDelete, {
      onSuccess: () => setVariablePendingDelete(null),
    });
  };

  const handleAddVariable = (): void => {
    onAddVariable();
  };

  const handleRowKeyDown = (event: KeyboardEvent<HTMLDivElement>, variable: VariableDto): void => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    onEditVariable(variable);
  };

  return (
    <div className="w-full min-w-0 px-2 py-2" data-widget-id={widgetId}>
      <div className="w-full flex mb-2">
        <Button
          variant="contained"
          startIcon={<Add />}
          color="primary"
          disableElevation
          data-dashboard-tour="variable-list-add"
          onClick={handleAddVariable}
        >
          {t('dashboard_add_variable', 'Add Variable')}
        </Button>
      </div>
      {variables.length === 0 && (
        <div className="w-full px-3 py-4 text-center">
          <Typography variant="body2" className="font-medium text-gray-600">
            {t('dashboard_no_variables_yet', 'No variables yet')}
          </Typography>
          <Typography variant="caption" className="text-gray-500">
            {t(
              'dashboard_no_variables_description',
              'Variables added to this dashboard will appear here.'
            )}
          </Typography>
        </div>
      )}
      {variables.map((variable: VariableDto) => {
        const values = getVariableValues(variable);
        const isMultiValue = Array.isArray(variable.value);
        const valueLabel = isMultiValue
          ? t('dashboard_variable_value_count', '{{count}} value', { count: values.length })
          : values[0] || t('dashboard_empty_value', 'Empty');
        const tooltipTitle =
          values.length > 0 ? values.join(', ') : t('dashboard_empty_value', 'Empty');
        const displayName = variable.label || variable.name;

        const row = (
          <div
            key={variable.id}
            role="button"
            tabIndex={0}
            className="group grid min-h-[34px] cursor-pointer grid-cols-[max-content_minmax(0,1fr)_2rem] items-center gap-2 border-0 border-b border-solid border-gray-100 px-2 py-1 last:border-b-0 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-cyan-500"
            data-dashboard-tour="variable-list-row"
            onClick={(): void => onEditVariable(variable)}
            onKeyDown={(event: KeyboardEvent<HTMLDivElement>): void =>
              handleRowKeyDown(event, variable)
            }
          >
            <Chip
              size="small"
              label={displayName}
              className="max-w-[13rem] bg-gray-100 text-xs font-medium text-gray-800"
              classes={{ label: 'truncate' }}
            />

            <Typography
              variant="body2"
              className="ml-auto max-w-[12rem] truncate text-right text-sm text-gray-700"
            >
              {valueLabel}
            </Typography>

            <IconButton
              size="small"
              aria-label={t('dashboard_open_variable_actions', 'Open {{name}} actions', {
                name: displayName,
              })}
              className="h-7 w-7 text-gray-500 hover:bg-gray-100"
              data-dashboard-tour="variable-list-actions"
              onClick={(event: React.MouseEvent<HTMLButtonElement>): void =>
                handleMenuOpen(event, variable)
              }
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </div>
        );

        if (!isMultiValue) {
          return row;
        }

        return (
          <Tooltip key={variable.id} title={tooltipTitle} placement="left" arrow>
            {row}
          </Tooltip>
        );
      })}

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('edit', 'Edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteOutline fontSize="small" className="text-red-600" />
          </ListItemIcon>
          <ListItemText className="text-red-600">{t('delete', 'Delete')}</ListItemText>
        </MenuItem>
      </Menu>

      <DashboardConfirmationDialog
        open={!!variablePendingDelete}
        title={t('dashboard_delete_variable', 'Delete Variable')}
        confirmLabel={t('dashboard_yes_delete', 'Yes, delete')}
        disabled={isDeletingVariable}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      >
        {t(
          'dashboard_variable_delete_confirmation_prefix',
          'This will permanently delete the variable'
        )}{' '}
        <strong>{variablePendingDelete?.label || variablePendingDelete?.name}</strong>{' '}
        {t(
          'dashboard_widget_delete_confirmation_suffix',
          'from the remote dashboard configuration. This action cannot be undone.'
        )}
      </DashboardConfirmationDialog>
    </div>
  );
};
